import React from "react";
import CodeMirror from "@uiw/react-codemirror";
import { EditorView, keymap, tooltips } from "@codemirror/view";
import {
  autocompletion,
  CompletionContext,
  completionKeymap,
  Completion,
  startCompletion,
} from "@codemirror/autocomplete";
import { syntaxHighlighting, HighlightStyle } from "@codemirror/language";
import { tags } from "@lezer/highlight";
import CustomTooltip from "@components/common/CustomTooltip";
import { HelpCircle } from "lucide-react";
import { getSharedTooltipHost } from "./sharedTooltipHost.ts";

interface ExpressionEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  availableFields?: Array<{ path: string; value: any }>; // Field paths and values from input data
  availableNodes?: Array<{
    id: string;
    title: string;
    outputData: any;
  }>; // Other nodes that can be referenced (legacy, prefer using nodes + currentNodeId)
  nodes?: Array<any>; // All nodes from React Flow - will auto-filter
  currentNodeId?: string; // Current node ID to exclude from availableNodes
  responseData?: any; // Pass this node's own response/output data to enable {{$response}}
  className?: string;
  minHeight?: string;
  maxHeight?: string;
  disabled?: boolean;
  validationType?: "json" | "xml" | "html" | "javascript" | "text" | "none";
}

const FILTERED_NODE_TITLES = ["start", "configuration"];
const FILTERED_NODE_TYPES = [
  "start",
  "configuration",
  "configurationnode",
  "confignode",
  "authentication",
];

// Custom syntax highlighting for expressions
const expressionHighlighting = HighlightStyle.define([
  { tag: tags.variableName, color: "var(--primary-green)" }, // was --primary-blue
  { tag: tags.string, color: "var(--primary-amber)" },
  { tag: tags.number, color: "var(--primary-amber)" },
  { tag: tags.keyword, color: "var(--primary-green)" }, // was --primary-blue
]);

// Helper to render completion with icon
const renderCompletionWithIcon = (
  completion: Completion,
  element: HTMLElement,
) => {
  element.innerHTML = "";

  // Create flex container
  const container = document.createElement("div");
  container.className = "cm-completion-container";

  // Get icon text based on type
  const getIconText = (type: string) => {
    switch (type) {
      case "string":
      case "text":
        return "Aa";
      case "number":
        return "123";
      case "boolean":
        return "T/F";
      case "array":
        return "[]";
      case "object":
        return "{}";
      case "keyword":
        return "$";
      case "method":
        return "ƒ";
      case "property":
        return "·";
      default:
        return "Aa";
    }
  };

  // Create icon element with data attribute and text content
  const iconSpan = document.createElement("span");
  iconSpan.className = "cm-completionIcon";
  iconSpan.textContent = getIconText(completion.type || "text");

  const type = completion.type || "text";

  if (
    ["string", "text", "number", "boolean", "array", "object"].includes(type)
  ) {
    iconSpan.style.color = "var(--primary-amber)";
  } else if (type === "keyword") {
    iconSpan.style.color = "var(--primary-blue)";
  } else if (type === "method") {
    iconSpan.style.color = "var(--primary-purple)";
  } else if (type === "property") {
    iconSpan.style.color = "var(--primary-indigo)";
  }

  iconSpan.style.fontSize = "10px";
  iconSpan.style.fontWeight = "700";
  iconSpan.style.display = "inline-flex";
  iconSpan.style.alignItems = "center";
  iconSpan.style.justifyContent = "center";

  container.appendChild(iconSpan);

  // Add label
  const labelSpan = document.createElement("span");
  labelSpan.className = "cm-completionLabel";
  labelSpan.textContent = completion.label;
  container.appendChild(labelSpan);

  // Add detail if present
  if (completion.detail) {
    const detailSpan = document.createElement("span");
    detailSpan.className = "cm-completionDetail";
    detailSpan.textContent = completion.detail;
    container.appendChild(detailSpan);
  }

  element.appendChild(container);
};

// Render function for disabled (non-selectable) completion items
const renderDisabledCompletion = (
  completion: Completion,
  element: HTMLElement,
) => {
  element.innerHTML = "";

  const container = document.createElement("div");
  container.className = "cm-completion-container cm-completion-disabled";
  container.style.opacity = "0.5";
  container.style.cursor = "not-allowed";

  // Icon
  const iconSpan = document.createElement("span");
  iconSpan.className = "cm-completionIcon";
  iconSpan.textContent = completion.type === "keyword" ? "⚠" : "ℹ";
  iconSpan.style.color = "var(--muted-foreground)";
  iconSpan.style.fontSize = "10px";
  iconSpan.style.fontWeight = "700";
  iconSpan.style.display = "inline-flex";
  iconSpan.style.alignItems = "center";
  iconSpan.style.justifyContent = "center";
  container.appendChild(iconSpan);

  // Label
  const labelSpan = document.createElement("span");
  labelSpan.className = "cm-completionLabel";
  labelSpan.textContent = completion.label;
  labelSpan.style.color = "var(--muted-foreground)";
  labelSpan.style.fontStyle = "italic";
  container.appendChild(labelSpan);

  // Detail
  if (completion.detail) {
    const detailSpan = document.createElement("span");
    detailSpan.className = "cm-completionDetail";
    detailSpan.textContent = completion.detail;
    detailSpan.style.color = "var(--muted-foreground)";
    container.appendChild(detailSpan);
  }

  element.appendChild(container);
};

const ExpressionEditorComponent: React.FC<ExpressionEditorProps> = ({
  value,
  onChange,
  placeholder = "Type {{ then $ to see suggestions, or drag fields here",
  availableFields = [],
  availableNodes: availableNodesProp = [],
  nodes,
  currentNodeId,
  responseData,
  className = "",
  minHeight = "32px",
  maxHeight = "200px",
  disabled = false,
  validationType = "none",
}) => {
  // Compute availableNodes from nodes if provided, otherwise use availableNodesProp
  const availableNodes = React.useMemo(() => {
    // Shared filter: removes system nodes that should never be referenceable via $()
    const filterSystemNodes = (
      nodeList: Array<{
        id: string;
        title: string;
        outputData: any;
        type?: string;
      }>,
    ) =>
      nodeList.filter((n) => {
        const titleLower = (n.title || "").toLowerCase();
        const typeLower = (n.type || "").toLowerCase();
        return (
          !FILTERED_NODE_TITLES.includes(titleLower) &&
          !FILTERED_NODE_TYPES.includes(typeLower)
        );
      });

    if (nodes && currentNodeId) {
      const mapped = nodes
        .filter((n: any) => n.id !== currentNodeId)
        .map((node: any) => ({
          id: node.id,
          type: node.type || "",
          title: node.data?.title || node.data?.header || "Untitled",
          outputData: node.data?.outputData || node.data?.response || null,
        }))
        .filter((n) => n.title && n.title !== "Untitled");

      return filterSystemNodes(mapped);
    }

    // Legacy prop path — title-based filter only (type not available)
    return filterSystemNodes(availableNodesProp);
  }, [nodes, currentNodeId, availableNodesProp]);

  // Ref to store the CodeMirror editor view for drag-and-drop
  const editorViewRef = React.useRef<any>(null);

  // Stable reference for onChange to prevent rerenders
  const onChangeRef = React.useRef(onChange);
  React.useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Stable reference for availableFields to prevent autocomplete reset
  const availableFieldsRef = React.useRef(availableFields);
  const availableFieldsHashRef = React.useRef("");

  React.useEffect(() => {
    const newHash = JSON.stringify(
      availableFields.map((f) => ({ path: f.path, hasValue: !!f.value })),
    );
    if (newHash !== availableFieldsHashRef.current) {
      availableFieldsRef.current = availableFields;
      availableFieldsHashRef.current = newHash;
    }
  }, [availableFields]);

  // Stable reference for responseData to prevent autocomplete reset
  const responseDataRef = React.useRef(responseData);
  React.useEffect(() => {
    responseDataRef.current = responseData;
  }, [responseData]);

  // Stable reference for availableNodes to prevent autocomplete reset
  const availableNodesRef = React.useRef(availableNodes);
  const availableNodesHashRef = React.useRef("");

  React.useEffect(() => {
    const newHash = JSON.stringify(
      availableNodes.map((n) => ({
        id: n.id,
        title: n.title,
        hasOutput: !!n.outputData,
      })),
    );
    if (newHash !== availableNodesHashRef.current) {
      availableNodesRef.current = availableNodes;
      availableNodesHashRef.current = newHash;
    }
  }, [availableNodes]);

  const handleChange = React.useCallback((val: string) => {
    onChangeRef.current(val);
  }, []);

  // Track focus and hover state for dynamic result preview height
  const [isFocused, setIsFocused] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);
  const [isDragOver, setIsDragOver] = React.useState(false);

  // Track validation errors
  const [validationError, setValidationError] = React.useState<string | null>(
    null,
  );

  // Validation function - validates content based on validationType
  const validateContent = React.useCallback(
    (
      content: string,
      type: string,
    ): { isValid: boolean; error: string | null } => {
      if (!content || !content.trim() || type === "none" || type === "text") {
        return { isValid: true, error: null };
      }

      // Helper to replace template variables with placeholders for validation
      const replaceTemplateVars = (
        str: string,
        replaceWith: string = "placeholder",
      ) => {
        return str.replace(/\{\{[^}]+\}\}/g, (match, offset) => {
          const beforeMatch = str.substring(0, offset);
          const afterMatch = str.substring(offset + match.length);

          // If wrapped in array brackets like [{{var}}], treat as array placeholder
          if (
            beforeMatch.trimEnd().endsWith("[") &&
            afterMatch.trimStart().startsWith("]")
          ) {
            return '"placeholder_item"';
          }

          // Check if this is a JSON value context (after a colon)
          const jsonValuePattern = /:\s*$/;
          if (jsonValuePattern.test(beforeMatch.trimEnd())) {
            return '["placeholder"]';
          }

          // If inside quotes, use quoted placeholder
          const lastQuote = beforeMatch.lastIndexOf('"');
          const lastEscape = beforeMatch.lastIndexOf("\\");
          if (
            lastQuote > lastEscape &&
            afterMatch.trimStart().startsWith('"')
          ) {
            return replaceWith;
          }

          return `"${replaceWith}"`;
        });
      };

      switch (type) {
        case "json":
          try {
            const testContent = replaceTemplateVars(content, "placeholder");
            JSON.parse(testContent);
            return { isValid: true, error: null };
          } catch (e: any) {
            return {
              isValid: false,
              error: `Invalid JSON format: ${e.message || "Syntax error"}`,
            };
          }

        case "xml":
          try {
            const testContent = replaceTemplateVars(content, "placeholder");
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(testContent, "text/xml");
            const parserError = xmlDoc.querySelector("parsererror");
            if (parserError) {
              return { isValid: false, error: "Invalid XML format" };
            }
            return { isValid: true, error: null };
          } catch (e) {
            return { isValid: false, error: "Invalid XML format" };
          }

        case "html":
          try {
            const testContent = replaceTemplateVars(content, "placeholder");

            if (testContent.trim().startsWith("<?xml")) {
              return {
                isValid: false,
                error: "XML content detected. Please use 'XML' type instead",
              };
            }

            const parser = new DOMParser();
            const htmlDoc = parser.parseFromString(testContent, "text/html");
            const parserError = htmlDoc.querySelector("parsererror");
            if (parserError) {
              return { isValid: false, error: "Invalid HTML format" };
            }
            return { isValid: true, error: null };
          } catch (e) {
            return { isValid: false, error: "Invalid HTML format" };
          }

        case "javascript":
          try {
            const testContent = replaceTemplateVars(content, '"placeholder"');
            new Function(testContent);
            return { isValid: true, error: null };
          } catch (e: any) {
            return {
              isValid: false,
              error: `Invalid JavaScript syntax: ${e.message || "Syntax error"}`,
            };
          }

        default:
          return { isValid: true, error: null };
      }
    },
    [],
  );

  // Validate content whenever value or validationType changes
  React.useEffect(() => {
    if (validationType && validationType !== "none") {
      const result = validateContent(value, validationType);
      setValidationError(result.isValid ? null : result.error);
    } else {
      setValidationError(null);
    }
  }, [value, validationType, validateContent]);

  // Auto-close all brackets intelligently
  const autoCloseBrackets = React.useMemo(() => {
    return EditorView.inputHandler.of((view, from, to, text) => {
      const state = view.state;
      const before = state.doc.sliceString(Math.max(0, from - 1), from);
      const after = state.doc.sliceString(
        to,
        Math.min(state.doc.length, to + 1),
      );

      // Check if we're inside {{ }} for bracket auto-close
      const textBeforeCursor = state.doc.sliceString(0, to);
      const lastOpenBraces = textBeforeCursor.lastIndexOf("{{");
      const lastCloseBraces = textBeforeCursor.lastIndexOf("}}");
      const insideExpression =
        lastOpenBraces !== -1 && lastCloseBraces < lastOpenBraces;

      if (text === "{") {
        // If previous character is '{' and next is '}', we're typing second { inside {}
        // Complete it to {{}}
        if (before === "{" && after === "}") {
          view.dispatch({
            changes: [
              { from: to, to: to, insert: "{" },
              { from: to + 1, to: to + 1, insert: "}" },
            ],
            selection: { anchor: to + 1 },
          });
          return true;
        }
        // Any other case - auto-complete single { to {}
        else {
          view.dispatch({
            changes: { from: to, to: to, insert: "{}" },
            selection: { anchor: to + 1 },
          });
          return true;
        }
      } else if (text === "[" && insideExpression) {
        // Auto-close square brackets (only inside {{ }})
        view.dispatch({
          changes: { from: to, to: to, insert: "[]" },
          selection: { anchor: to + 1 },
        });
        return true;
      } else if (text === "(" && insideExpression) {
        // Auto-close parentheses (only inside {{ }})
        view.dispatch({
          changes: { from: to, to: to, insert: "()" },
          selection: { anchor: to + 1 },
        });
        return true;
      } else if (text === ")" && insideExpression) {
        // Check if we just closed a node reference like $("NodeName")
        // Trigger autocomplete to show .json and . options
        const textBeforeClosing = state.doc.sliceString(0, to);
        if (/\$\("[^"]+"\s*$/.test(textBeforeClosing)) {
          // Let the ) be inserted normally, then trigger autocomplete
          requestAnimationFrame(() => {
            const match = textBeforeClosing.match(/\$\("([^"]+)"\s*$/);
            if (match) {
              // Trigger autocomplete after ) is inserted
              setTimeout(() => {
                startCompletion(view);
              }, 10);
            }
          });
        }
      }
      return false;
    });
  }, []);

  // Helper to get value at a specific path in the data
  const getValueAtPath = React.useCallback(
    (path: string, rootData?: any): any => {
      // If rootData is provided (for node references), use it
      // Otherwise use availableFieldsRef (for $json) or responseDataRef (for $response)
      let current: any;

      if (rootData !== undefined) {
        current = rootData;
      } else if (path.startsWith("$response")) {
        current = responseDataRef.current;
        if (current === undefined || current === null) {
          return undefined;
        }
      } else {
        const fields = availableFieldsRef.current;
        if (!fields || fields.length === 0) {
          return undefined;
        }

        // Get root data
        const rootField = fields.find((f) => f.path === "$json");
        if (!rootField || !rootField.value) {
          return undefined;
        }

        current = rootField.value;
      }

      // Auto-unwrap single-element arrays for better UX
      if (Array.isArray(current) && current.length === 1) {
        current = current[0];
      }

      // Remove $json/$response prefix and clean the path
      const cleanPath = path
        .replace(/^\$response\.?/, "")
        .replace(/^\$response/, "")
        .replace(/^\$json\.?/, "")
        .replace(/^\$json/, "");
      if (!cleanPath) {
        return current; // Just $json / $response
      }

      // Parse path: supports "item.vehicles[0].manufacturer.service_centers[1].city"
      // Also handles leading-bracket paths like "[1].todo" produced by json[ stripping
      const tokens: Array<{
        type: "property" | "index";
        value: string | number;
      }> = [];

      // Normalise: if path starts with "[" add a synthetic leading dot so the
      // split-by-dot logic below doesn't produce an empty first segment
      let tempPath = cleanPath.startsWith("[")
        ? "__ROOT__" + cleanPath
        : cleanPath;
      const arrayMatches: Array<{ placeholder: string; index: number }> = [];
      let counter = 0;

      // Extract all array indices and replace with placeholders
      tempPath = tempPath.replace(/\[(\d+)\]/g, (_match, index) => {
        const placeholder = `__ARRAY_INDEX_${counter}__`;
        arrayMatches.push({ placeholder, index: parseInt(index) });
        counter++;
        return `.${placeholder}`;
      });

      // Split by dot and process each segment
      const segments = tempPath.split(".").filter((s) => s && s !== "__ROOT__");

      segments.forEach((segment) => {
        const arrayMatch = arrayMatches.find(
          (am) => am.placeholder === segment,
        );
        if (arrayMatch) {
          tokens.push({ type: "index", value: arrayMatch.index });
        } else if (segment) {
          tokens.push({ type: "property", value: segment });
        }
      });

      // Traverse the path using tokens
      for (const token of tokens) {
        if (current === undefined || current === null) {
          return undefined;
        }

        if (token.type === "index") {
          if (Array.isArray(current)) {
            current = current[token.value as number];
          } else {
            return undefined;
          }
        } else {
          if (typeof current === "object" && !Array.isArray(current)) {
            current = current[token.value as string];
          } else {
            return undefined;
          }
        }
      }

      return current;
    },
    [], // No dependencies - uses ref
  );

  // Evaluate expression and get result
  // Helper to evaluate a single expression (without {{ }})
  const evaluateSingleExpression = React.useCallback(
    (expression: string): any => {
      try {
        const trimmedExpr = expression.trim();

        // Autocomplete may leave temporary trailing path characters (e.g. "." or "[")
        // while the user is still selecting nested fields. Normalize these so preview works.
        const sanitizePathForPreview = (rawPath: string): string => {
          let path = rawPath.trim().replace(/\.\[/g, "[");

          while (path.endsWith(".") || path.endsWith("[")) {
            path = path.slice(0, -1);
          }

          // If there is an unmatched trailing bracket expression, trim it for preview.
          const lastOpenBracket = path.lastIndexOf("[");
          const lastCloseBracket = path.lastIndexOf("]");
          if (lastOpenBracket > lastCloseBracket) {
            path = path.slice(0, lastOpenBracket);
          }

          return path;
        };

        // Config-style references like "config.username" or any expression that
        // doesn't start with "$" are valid pass-throughs (e.g. from Configuration
        // node). They are not resolvable here but must not be flagged as invalid.
        if (!trimmedExpr.startsWith("$")) {
          return "__passthrough__";
        }

        // Handle $response paths
        if (trimmedExpr.startsWith("$response")) {
          const respData = responseDataRef.current;
          if (respData === undefined || respData === null) return null;

          let current = respData;

          // Auto-unwrap single-element arrays (Pattern 2: wrapper objects)
          if (Array.isArray(current) && current.length === 1) {
            current = current[0];
          }

          // Just $response
          if (trimmedExpr === "$response") {
            return current;
          }

          // Parse path after $response
          const path = trimmedExpr.substring(9); // Remove "$response"
          if (!path || path === ".") return current;

          // Clean path
          const cleanPath = sanitizePathForPreview(
            path.startsWith(".") ? path.substring(1) : path,
          );
          if (!cleanPath) return current;

          // If current is still an array (Pattern 1: array of items), default to first item
          // only when the user did not explicitly provide an index path like [1].id
          if (
            Array.isArray(current) &&
            current.length > 0 &&
            !cleanPath.startsWith("[")
          ) {
            const firstItem = current[0];
            if (firstItem && typeof firstItem === "object") {
              return getValueAtPath(cleanPath, firstItem);
            }
          }

          return getValueAtPath(cleanPath, current);
        }

        // Handle $json paths
        if (trimmedExpr.startsWith("$json")) {
          const fields = availableFieldsRef.current;
          if (!fields || fields.length === 0) return null;

          const rootField = fields.find((f) => f.path === "$json");
          if (!rootField || !rootField.value) return null;

          let current = rootField.value;

          // Auto-unwrap single-element arrays (Pattern 2: wrapper objects)
          if (Array.isArray(current) && current.length === 1) {
            current = current[0];
          }

          // Just $json
          if (trimmedExpr === "$json") {
            return current;
          }

          // Parse path after $json
          const path = trimmedExpr.substring(5); // Remove "$json"
          if (!path || path === ".") return current;

          // Clean path
          const cleanPath = sanitizePathForPreview(
            path.startsWith(".") ? path.substring(1) : path,
          );
          if (!cleanPath) return current;

          // If current is still an array (Pattern 1: array of items), default to first item
          // only when the user did not explicitly provide an index path like [1].id
          if (
            Array.isArray(current) &&
            current.length > 0 &&
            !cleanPath.startsWith("[")
          ) {
            const firstItem = current[0];
            if (firstItem && typeof firstItem === "object") {
              return getValueAtPath(cleanPath, firstItem);
            }
          }

          return getValueAtPath(cleanPath, current);
        }

        // Handle $("NodeName").path
        const nodeMatch = trimmedExpr.match(/^\$\("([^"]+)"\)\.?(.*)$/);
        if (nodeMatch) {
          const [, nodeName, pathInNode] = nodeMatch;
          const availableNodes = availableNodesRef.current || [];
          const targetNode = availableNodes.find((n) => n.title === nodeName);

          if (!targetNode || !targetNode.outputData) return null;

          let current = targetNode.outputData;

          // Auto-unwrap single-element arrays (Pattern 2: wrapper objects)
          if (Array.isArray(current) && current.length === 1) {
            current = current[0];
          }

          if (!pathInNode) return current;

          // Handle "json" prefix
          let actualPath = sanitizePathForPreview(pathInNode);
          if (pathInNode === "json") {
            return current;
          } else if (pathInNode.startsWith("json.")) {
            actualPath = sanitizePathForPreview(pathInNode.substring(5)); // Remove "json."
          } else if (pathInNode.startsWith("json[")) {
            actualPath = sanitizePathForPreview(pathInNode.substring(4)); // Remove "json" → leaves "[1].todo"
            // Leading bracket e.g. "[1].todo" — getValueAtPath expects no leading dot/bracket
            // Convert "[1].todo" → traverse array index then property path
            if (actualPath.startsWith("[")) {
              const bracketEnd = actualPath.indexOf("]");
              if (bracketEnd !== -1) {
                const arrayIndex = parseInt(
                  actualPath.substring(1, bracketEnd),
                  10,
                );
                const rest = actualPath
                  .substring(bracketEnd + 1)
                  .replace(/^\./, ""); // remove leading dot
                if (Array.isArray(current)) {
                  current = current[arrayIndex];
                } else if (
                  Array.isArray(current) === false &&
                  typeof current === "object"
                ) {
                  current = (current as any)[arrayIndex];
                }
                if (!rest) return current;
                return getValueAtPath(rest, current);
              }
            }
          }

          if (!actualPath) return current;

          // If current is still an array (Pattern 1: array of items), default to first item
          // only when the user did not explicitly provide an index path like [1].id
          if (
            Array.isArray(current) &&
            current.length > 0 &&
            !actualPath.startsWith("[")
          ) {
            const firstItem = current[0];
            if (firstItem && typeof firstItem === "object") {
              return getValueAtPath(actualPath, firstItem);
            }
          }

          return getValueAtPath(actualPath, current);
        }

        return null;
      } catch (error) {
        return null;
      }
    },
    [getValueAtPath],
  );

  // Evaluate expression and get result - supports multiple expressions
  const evaluateExpression = React.useCallback(
    (expr: string): any => {
      try {
        // Check if there are any expressions
        if (!expr.includes("{{")) return null;

        // Find all {{...}} expressions
        const expressionRegex = /\{\{(.+?)\}\}/g;
        const matches = Array.from(expr.matchAll(expressionRegex));

        if (matches.length === 0) return null;

        // If there's only one expression and it's the entire string, return the evaluated value
        if (matches.length === 1 && expr.trim() === matches[0][0]) {
          const expression = matches[0][1];
          return evaluateSingleExpression(expression);
        }

        // Multiple expressions or mixed text - concatenate results
        let result = expr;
        let hasAnyResult = false;

        // Replace each expression with its evaluated result
        for (const match of matches) {
          const fullMatch = match[0]; // {{...}}
          const expression = match[1]; // content inside
          const value = evaluateSingleExpression(expression);

          if (value === "__passthrough__") {
            // Config-style reference: valid but not resolvable here.
            // Leave the original {{...}} text in place and count as a result
            // so the expression is not considered invalid.
            hasAnyResult = true;
          } else if (value !== null && value !== undefined) {
            hasAnyResult = true;
            // Convert value to string for concatenation
            let stringValue = "";
            if (typeof value === "object") {
              stringValue = JSON.stringify(value);
            } else {
              stringValue = String(value);
            }
            result = result.replace(fullMatch, stringValue);
          } else {
            // If evaluation fails, leave the expression as-is
            // Or optionally replace with empty string or error message
            result = result.replace(fullMatch, "[Error: Cannot evaluate]");
          }
        }

        return hasAnyResult ? result : null;
      } catch (error) {
        return null;
      }
    },
    [evaluateSingleExpression],
  );

  // Format result for display
  const formatResult = React.useCallback((result: any): string => {
    if (result === null || result === undefined) {
      return "";
    }

    try {
      // If result is already a string (from multiple expressions), return as-is
      if (typeof result === "string") {
        return result;
      }

      if (Array.isArray(result)) {
        return `[Array: ${JSON.stringify(result, null, 2)}]`;
      } else if (typeof result === "object") {
        return `[Object: ${JSON.stringify(result, null, 2)}]`;
      } else {
        return String(result);
      }
    } catch (error) {
      return "";
    }
  }, []);

  // Evaluate current expression
  const hasExpression = React.useMemo(() => {
    return value.includes("{{") && value.includes("}}");
  }, [value]);

  const isInvalidExpression = React.useMemo(() => {
    if (!value.includes("{{")) return false;
    const expressionRegex = /\{\{(.+?)\}\}/g;
    const matches = Array.from(value.matchAll(expressionRegex));

    for (const match of matches) {
      const expr = match[1].trim();

      // $json expression but no input data yet — not invalid, just not loaded
      if (expr.startsWith("$json")) {
        const hasInput = availableFields.some(
          (f) => f.path === "$json" && f.value,
        );
        if (!hasInput) return false;
      }

      // $response expression but no response data yet — not invalid, just not loaded
      if (expr.startsWith("$response")) {
        const hasResponse = responseData !== undefined && responseData !== null;
        if (!hasResponse) return false;
      }

      // $("Node") expression but node has no output yet — not invalid
      const nodeMatch = expr.match(/^\$\("([^"]+)"\)/);
      if (nodeMatch) {
        const nodeName = nodeMatch[1];
        const node = availableNodes.find((n) => n.title === nodeName);
        if (!node?.outputData) return false;
      }

      // config-style passthrough — not invalid
      if (!expr.startsWith("$")) return false;
    }

    const result = evaluateExpression(value);
    if (result === "__passthrough__") return false;
    return result === null || result === undefined;
  }, [
    value,
    evaluateExpression,
    availableFields,
    availableNodes,
    responseData,
  ]);

  const resultPreview = React.useMemo(() => {
    if (isInvalidExpression) return "[invalid syntax]";

    const usesJson = value.includes("$json");
    const hasInput = availableFields.some((f) => f.path === "$json" && f.value);
    if (usesJson && !hasInput) return "";

    const usesResponse = value.includes("$response");
    const hasResponseData = responseData !== undefined && responseData !== null;
    if (usesResponse && !hasResponseData) return "";

    const result = evaluateExpression(value);
    if (result === "__passthrough__") return "";
    return formatResult(result);
  }, [
    value,
    evaluateExpression,
    formatResult,
    isInvalidExpression,
    availableFields,
    responseData,
  ]);
  // Helper to extract fields from any value (object, array, primitive)
  const extractFieldsFromValue = React.useCallback(
    (value: any, basePath: string): Array<{ path: string; value: any }> => {
      const fields: Array<{ path: string; value: any }> = [];

      if (value === null || value === undefined) {
        return fields;
      }

      if (Array.isArray(value)) {
        // For arrays, show indices and extract fields from first item
        value.forEach((item, index) => {
          fields.push({
            path: `${basePath}[${index}]`,
            value: item,
          });
        });

        // Also extract common fields from first item for convenience
        if (
          value.length > 0 &&
          typeof value[0] === "object" &&
          value[0] !== null
        ) {
          Object.keys(value[0]).forEach((key) => {
            fields.push({
              path: `${basePath}[0].${key}`,
              value: value[0][key],
            });
          });
        }
      } else if (typeof value === "object") {
        // For objects, extract all properties
        Object.entries(value).forEach(([key, val]) => {
          fields.push({
            path: `${basePath}.${key}`,
            value: val,
          });

          // Recursively extract nested fields (limit depth to 2 levels)
          if (
            val &&
            typeof val === "object" &&
            basePath.split(".").length < 3
          ) {
            const nestedFields = extractFieldsFromValue(
              val,
              `${basePath}.${key}`,
            );
            fields.push(...nestedFields);
          }
        });
      }

      return fields;
    },
    [],
  );

  // Field autocomplete function
  const fieldAutocomplete = React.useMemo(() => {
    return autocompletion({
      override: [
        (context: CompletionContext) => {
          const textBeforeCursor = context.state.doc.sliceString(
            0,
            context.pos,
          );

          const lastOpenBraces = textBeforeCursor.lastIndexOf("{{");
          const lastCloseBraces = textBeforeCursor.lastIndexOf("}}");

          if (lastOpenBraces === -1 || lastCloseBraces > lastOpenBraces) {
            return null;
          }

          // Extract the expression text (everything after {{ and before cursor)
          const expressionText = textBeforeCursor.substring(lastOpenBraces + 2);
          const trimmedExpr = expressionText.trim();

          const normalizedExpr = trimmedExpr
            .replace(/\s*\.\s*/g, ".")
            .replace(/\[\s*/g, "[")
            .replace(/\s*\]/g, "]")
            .replace(/\$\(\s*/g, "$(") // Remove space after $(
            .replace(/\s*"\)/g, '")'); // Remove space before ")

          // ============================================
          // NODE REFERENCE AUTOCOMPLETE
          // ============================================

          // 1. Typing "$(" - suggest available nodes
          if (trimmedExpr === "$(") {
            const availableNodes = (availableNodesRef.current || []).filter(
              (node) =>
                !FILTERED_NODE_TITLES.includes(node.title.toLowerCase()),
            );

            if (availableNodes.length > 0) {
              const options = availableNodes.map((node) => {
                let detail = "";
                if (node.outputData) {
                  if (Array.isArray(node.outputData)) {
                    detail = `Array[${node.outputData.length}]`;
                  } else if (typeof node.outputData === "object") {
                    const keys = Object.keys(node.outputData).slice(0, 3);
                    detail = `{${keys.join(", ")}}${Object.keys(node.outputData).length > 3 ? "..." : ""}`;
                  }
                } else {
                  detail = "⚠ Not executed yet";
                }

                return {
                  label: node.title,
                  type: "keyword",
                  detail,
                  apply: node.outputData
                    ? (
                        view: any,
                        _completion: any,
                        from: number,
                        to: number,
                      ) => {
                        // Insert node name with closing quote and parenthesis
                        const insertText = `"${node.title}")`;
                        view.dispatch({
                          changes: { from, to, insert: insertText },
                          selection: { anchor: from + insertText.length },
                        });
                        // Trigger autocomplete to show dot suggestion - use requestAnimationFrame for better timing
                        requestAnimationFrame(() => {
                          startCompletion(view);
                        });
                      }
                    : `"${node.title}")`,
                  boost: node.outputData ? 100 : 50, // Prioritize executed nodes
                  render: renderCompletionWithIcon,
                };
              });

              return {
                from: context.pos,
                options,
                validFor: () => false,
              };
            }
          }

          // 2. Typing inside "$("..." - filter node names
          const nodeNameTypingMatch = trimmedExpr.match(/^\$\("([^"]*)$/);
          if (nodeNameTypingMatch) {
            const partialNodeName = nodeNameTypingMatch[1].toLowerCase();
            const availableNodes = (availableNodesRef.current || []).filter(
              (node) =>
                !FILTERED_NODE_TITLES.includes(node.title.toLowerCase()),
            );

            const filteredNodes = availableNodes.filter((node) =>
              node.title.toLowerCase().includes(partialNodeName),
            );

            if (filteredNodes.length > 0) {
              const options = filteredNodes.map((node) => {
                let detail = "";
                if (node.outputData) {
                  if (Array.isArray(node.outputData)) {
                    detail = `Array[${node.outputData.length}]`;
                  } else if (typeof node.outputData === "object") {
                    const keys = Object.keys(node.outputData).slice(0, 3);
                    detail = `{${keys.join(", ")}}${Object.keys(node.outputData).length > 3 ? "..." : ""}`;
                  }
                } else {
                  detail = "⚠ Not executed yet";
                }

                return {
                  label: node.title,
                  type: "keyword",
                  detail,
                  apply: node.outputData
                    ? (
                        view: any,
                        _completion: any,
                        from: number,
                        to: number,
                      ) => {
                        // Insert node name with closing quote and parenthesis
                        const insertText = `"${node.title}")`;
                        view.dispatch({
                          changes: { from, to, insert: insertText },
                          selection: { anchor: from + insertText.length },
                        });
                        // Trigger autocomplete to show dot suggestion - use requestAnimationFrame for better timing
                        requestAnimationFrame(() => {
                          startCompletion(view);
                        });
                      }
                    : `"${node.title}")`,
                  boost: node.outputData ? 100 : 50, // Prioritize executed nodes
                  render: renderCompletionWithIcon,
                };
              });

              return {
                from: context.pos - partialNodeName.length,
                options,
                validFor: /^[^"]*$/,
              };
            }
          }

          // 3. After node name "$("NodeName")" - suggest dot
          const afterNodeMatch = trimmedExpr.match(/^\$\("([^"]+)"\)\s*$/);
          if (afterNodeMatch) {
            const nodeName = afterNodeMatch[1];
            const availableNodes = availableNodesRef.current || [];
            const targetNode = availableNodes.find((n) => n.title === nodeName);

            if (targetNode) {
              if (targetNode.outputData) {
                const isArray = Array.isArray(targetNode.outputData);
                const isObject =
                  typeof targetNode.outputData === "object" && !isArray;

                return {
                  from: context.pos,
                  options: [
                    {
                      label: ".json",
                      type: "property",
                      detail: isArray
                        ? `Array[${targetNode.outputData.length}]`
                        : isObject
                          ? `{${Object.keys(targetNode.outputData).slice(0, 3).join(", ")}...}`
                          : "Output data",
                      apply: (
                        view: any,
                        _completion: any,
                        from: number,
                        to: number,
                      ) => {
                        const insertText = ".json";
                        view.dispatch({
                          changes: { from, to, insert: insertText },
                          selection: { anchor: from + insertText.length },
                        });
                        setTimeout(() => {
                          startCompletion(view);
                        }, 50);
                      },
                      boost: 100,
                      render: renderCompletionWithIcon,
                    },
                    {
                      label: "",
                      type: "keyword",
                      detail: "Access fields directly",
                      apply: "",
                      boost: 90,
                      render: renderCompletionWithIcon,
                    },
                  ],
                  validFor: () => false,
                };
              } else {
                // Node exists but hasn't been executed yet
                return {
                  from: context.pos,
                  options: [
                    {
                      label: "Node not executed yet",
                      type: "keyword",
                      detail: `Execute "${nodeName}" first to see fields`,
                      apply: () => {}, // No-op function - does nothing when selected
                      boost: -1000, // Very low boost to appear last
                      render: renderDisabledCompletion,
                    },
                  ],
                  validFor: () => false,
                };
              }
            }
          }

          // 4. Navigate node output fields "$("Node").field.subfield"
          const nodeFieldMatch = normalizedExpr.match(
            /^\$\("([^"]+)"\)\.(.*)$/,
          );

          if (nodeFieldMatch) {
            const [, nodeName, pathInNode] = nodeFieldMatch;
            const availableNodes = availableNodesRef.current || [];
            const targetNode = availableNodes.find((n) => n.title === nodeName);

            if (targetNode) {
              if (!targetNode.outputData) {
                // Show helper message when node has no output
                return {
                  from: context.pos,
                  options: [
                    {
                      label: "No output data",
                      type: "keyword",
                      detail: `Execute "${nodeName}" first`,
                      apply: () => {}, // No-op function - does nothing when selected
                      boost: -1000, // Very low boost to appear last
                      render: renderDisabledCompletion,
                    },
                  ],
                  validFor: () => false,
                };
              }

              // Handle typing after dot (including first dot)
              // Also handle "json" which doesn't need a dot before array/object access
              if (
                pathInNode === "" ||
                pathInNode === "json" ||
                pathInNode.endsWith(".")
              ) {
                // Clean up the path: remove trailing dot and fix ".[" to "["
                let pathBeforeDot =
                  pathInNode === ""
                    ? ""
                    : pathInNode === "json"
                      ? "json"
                      : pathInNode.slice(0, -1);
                // Fix pattern like "jsontodos.[1" to "jsontodos[1"
                pathBeforeDot = pathBeforeDot.replace(/\.\[/g, "[");

                // Special case: At root level, suggest "json" as the accessor
                if (pathInNode === "") {
                  return {
                    from: context.pos,
                    options: [
                      {
                        label: "json",
                        type: "property",
                        detail: Array.isArray(targetNode.outputData)
                          ? `Array[${targetNode.outputData.length}]`
                          : typeof targetNode.outputData === "object"
                            ? `{${Object.keys(targetNode.outputData).slice(0, 3).join(", ")}...}`
                            : "Output data",
                        apply: (
                          view: any,
                          _completion: any,
                          from: number,
                          to: number,
                        ) => {
                          const insertText = "json";
                          view.dispatch({
                            changes: { from, to, insert: insertText },
                            selection: { anchor: from + insertText.length },
                          });
                          setTimeout(() => {
                            startCompletion(view);
                          }, 50);
                        },
                        boost: 100,
                        render: renderCompletionWithIcon,
                      },
                    ],
                    validFor: /^[a-zA-Z_$][\w$]*$/,
                  };
                }

                // Special handling for paths like "jsontodos" that should be "json.todos"
                let actualPathBeforeDot = pathBeforeDot;
                let needsPathFix = false;
                let needsDotPrefix = false;

                if (
                  pathBeforeDot &&
                  pathBeforeDot.startsWith("json") &&
                  !pathBeforeDot.startsWith("json.") &&
                  !pathBeforeDot.startsWith("json[") &&
                  pathBeforeDot.length > 4
                ) {
                  actualPathBeforeDot = "json." + pathBeforeDot.substring(4);
                  needsPathFix = true;
                } else if (pathBeforeDot === "json" && pathInNode === "json") {
                  needsDotPrefix = true;
                }

                // Handle paths starting with "json" - use outputData as root
                let valueAtPath;
                if (
                  actualPathBeforeDot === "json" ||
                  actualPathBeforeDot.startsWith("json.") ||
                  actualPathBeforeDot.startsWith("json[")
                ) {
                  // Strip "json" prefix and navigate from outputData
                  let actualPath = "";
                  if (actualPathBeforeDot === "json") {
                    actualPath = "";
                  } else if (actualPathBeforeDot.startsWith("json.")) {
                    actualPath = actualPathBeforeDot.substring(5); // Remove "json."
                  } else if (actualPathBeforeDot.startsWith("json[")) {
                    actualPath = actualPathBeforeDot.substring(4); // Remove "json"
                  }
                  valueAtPath = actualPath
                    ? getValueAtPath(actualPath, targetNode.outputData)
                    : targetNode.outputData;
                } else {
                  // Regular path navigation
                  valueAtPath = actualPathBeforeDot
                    ? getValueAtPath(actualPathBeforeDot, targetNode.outputData)
                    : targetNode.outputData;
                }

                // Auto-unwrap single-element arrays for better UX
                if (Array.isArray(valueAtPath) && valueAtPath.length === 1) {
                  valueAtPath = valueAtPath[0];
                }

                try {
                  if (Array.isArray(valueAtPath)) {
                    const options = valueAtPath.map((item, index) => {
                      let detail = "";
                      const isObject =
                        item &&
                        typeof item === "object" &&
                        !Array.isArray(item);
                      const isArray = Array.isArray(item);

                      if (isObject) {
                        const keys = Object.keys(item).slice(0, 3);
                        detail = `[${index}] {${keys.join(", ")}}${Object.keys(item).length > 3 ? "..." : ""}`;
                      } else if (isArray) {
                        detail = `[${index}] Array[${item.length}]`;
                      } else {
                        detail = `[${index}]`;
                      }

                      const shouldAutoCompleteDot = isObject;
                      const shouldAutoCompleteBracket = isArray;

                      return {
                        label: `[${index}]`,
                        type: "property",
                        detail,
                        apply: (
                          view: any,
                          _completion: any,
                          from: number,
                          to: number,
                        ) => {
                          let suffix = "";
                          if (shouldAutoCompleteDot) suffix = ".";
                          else if (shouldAutoCompleteBracket) suffix = "[";

                          const insertText = needsPathFix
                            ? `${actualPathBeforeDot}[${index}]${suffix}`
                            : needsDotPrefix
                              ? `[${index}]${suffix}`
                              : `[${index}]${suffix}`;

                          const replaceFrom = needsPathFix
                            ? from - pathBeforeDot.length - 1
                            : needsDotPrefix
                              ? from
                              : from - 1;

                          view.dispatch({
                            changes: {
                              from: replaceFrom,
                              to,
                              insert: insertText,
                            },
                            selection: {
                              anchor: replaceFrom + insertText.length,
                            },
                          });

                          if (
                            shouldAutoCompleteDot ||
                            shouldAutoCompleteBracket
                          ) {
                            setTimeout(() => {
                              startCompletion(view);
                            }, 50);
                          }
                        },
                        boost: 100 - index,
                        render: renderCompletionWithIcon,
                      };
                    });

                    return {
                      from: context.pos,
                      options: options.slice(0, 50),
                      validFor: () => false,
                    };
                  }

                  // Object suggestion
                  if (typeof valueAtPath === "object" && valueAtPath !== null) {
                    const options: any[] = [];

                    Object.entries(valueAtPath).forEach(([key, val]) => {
                      let type = "text";
                      if (Array.isArray(val)) type = "array";
                      else if (typeof val === "object" && val !== null)
                        type = "object";
                      else if (typeof val === "number") type = "number";
                      else if (typeof val === "boolean") type = "boolean";
                      else if (typeof val === "string") type = "string";

                      let detail = "";
                      if (Array.isArray(val)) {
                        detail = `Array[${val.length}]`;
                      } else if (typeof val === "object" && val !== null) {
                        const keys = Object.keys(val).slice(0, 3);
                        detail = `{${keys.join(", ")}}${Object.keys(val).length > 3 ? "..." : ""}`;
                      } else {
                        const str = String(val);
                        detail =
                          str.length > 20 ? str.substring(0, 20) + "..." : str;
                      }

                      const shouldAutoComplete = type === "object";
                      const isArray = type === "array";

                      options.push({
                        label: key,
                        type,
                        detail,
                        apply: shouldAutoComplete
                          ? (
                              view: any,
                              _completion: any,
                              from: number,
                              to: number,
                            ) => {
                              const insertText = needsPathFix
                                ? `${actualPathBeforeDot}.${key}.`
                                : needsDotPrefix
                                  ? `.${key}.`
                                  : `${key}.`;
                              view.dispatch({
                                changes: { from, to, insert: insertText },
                                selection: { anchor: from + insertText.length },
                              });
                              setTimeout(() => {
                                startCompletion(view);
                              }, 50);
                            }
                          : isArray
                            ? (
                                view: any,
                                _completion: any,
                                from: number,
                                to: number,
                              ) => {
                                const insertText = needsPathFix
                                  ? `${actualPathBeforeDot}.${key}[`
                                  : needsDotPrefix
                                    ? `.${key}[`
                                    : `${key}[`;
                                view.dispatch({
                                  changes: { from, to, insert: insertText },
                                  selection: {
                                    anchor: from + insertText.length,
                                  },
                                });
                                setTimeout(() => {
                                  startCompletion(view);
                                }, 50);
                              }
                            : needsPathFix
                              ? `${actualPathBeforeDot}.${key}`
                              : needsDotPrefix
                                ? `.${key}`
                                : key,
                        boost: 100,
                        render: renderCompletionWithIcon,
                      });
                    });

                    if (options.length > 0) {
                      const fromPos = needsPathFix
                        ? context.pos - pathBeforeDot.length - 1
                        : context.pos;
                      return {
                        from: fromPos,
                        options,
                        validFor: /^[a-zA-Z_$][\w$]*$/,
                      };
                    }
                  }

                  // If we got here and needsPathFix is true, the corrected path doesn't exist
                  if (
                    needsPathFix &&
                    (valueAtPath === undefined || valueAtPath === null)
                  ) {
                    const suggestedPath = actualPathBeforeDot;
                    return {
                      from: context.pos - pathBeforeDot.length - 1,
                      options: [
                        {
                          label: `❌ Invalid path "${pathBeforeDot}"`,
                          type: "keyword",
                          detail: `Did you mean "${suggestedPath}"? Field not found.`,
                          apply: "",
                          boost: 0,
                          render: renderCompletionWithIcon,
                        },
                      ],
                      validFor: () => false,
                    };
                  }
                } catch (error) {
                  // Silently handle autocomplete errors
                }
              } else {
                // Handle typing partial field name "$("Node").fie" -> filter and show matching fields
                // Find the last dot to split path
                const lastDotIndex = pathInNode.lastIndexOf(".");
                let pathBeforeDot = "";
                let partialField = pathInNode;

                if (lastDotIndex !== -1) {
                  pathBeforeDot = pathInNode.substring(0, lastDotIndex);
                  partialField = pathInNode.substring(lastDotIndex + 1);
                  // Fix pattern like ".[1]" to "[1]"
                  pathBeforeDot = pathBeforeDot.replace(/\.\[/g, "[");
                }

                // Track original partial field length for correct cursor positioning
                let originalPartialLength = partialField.length;

                // Special case: If typing "jsontodos" etc - split "json" from the rest
                if (
                  pathBeforeDot === "" &&
                  partialField.startsWith("json") &&
                  partialField.length > 4
                ) {
                  originalPartialLength = partialField.length;
                  pathBeforeDot = "json";
                  partialField = partialField.substring(4); // Remove "json" prefix
                }

                // Special case: "json" is a complete accessor, suggest adding dot
                if (pathBeforeDot === "" && partialField === "json") {
                  return null;
                }

                // Special case: At root level typing partial "json"
                if (
                  pathBeforeDot === "" &&
                  partialField.toLowerCase().startsWith("j") &&
                  partialField.length < 4
                ) {
                  if ("json".startsWith(partialField.toLowerCase())) {
                    return {
                      from: context.pos - partialField.length,
                      options: [
                        {
                          label: "json",
                          type: "property",
                          detail: Array.isArray(targetNode.outputData)
                            ? `Array[${targetNode.outputData.length}]`
                            : typeof targetNode.outputData === "object"
                              ? `{${Object.keys(targetNode.outputData).slice(0, 3).join(", ")}...}`
                              : "Output data",
                          apply: (
                            view: any,
                            _completion: any,
                            from: number,
                            to: number,
                          ) => {
                            const insertText = "json";
                            view.dispatch({
                              changes: { from, to, insert: insertText },
                              selection: { anchor: from + insertText.length },
                            });
                            setTimeout(() => {
                              startCompletion(view);
                            }, 50);
                          },
                          boost: 100,
                          render: renderCompletionWithIcon,
                        },
                      ],
                      validFor: /^[a-zA-Z_$][\w$]*$/,
                    };
                  }
                }

                // Special handling for paths like "jsontodos" in partial typing too
                let actualPathBeforeDotPartial = pathBeforeDot;
                if (
                  pathBeforeDot &&
                  pathBeforeDot.startsWith("json") &&
                  !pathBeforeDot.startsWith("json.") &&
                  !pathBeforeDot.startsWith("json[") &&
                  pathBeforeDot.length > 4
                ) {
                  actualPathBeforeDotPartial =
                    "json." + pathBeforeDot.substring(4);
                }

                // Handle paths starting with "json" - use outputData as root
                let valueAtPath;
                if (
                  actualPathBeforeDotPartial === "json" ||
                  actualPathBeforeDotPartial.startsWith("json.") ||
                  actualPathBeforeDotPartial.startsWith("json[")
                ) {
                  // Strip "json" prefix and navigate from outputData
                  let actualPath = "";
                  if (actualPathBeforeDotPartial === "json") {
                    actualPath = "";
                  } else if (actualPathBeforeDotPartial.startsWith("json.")) {
                    actualPath = actualPathBeforeDotPartial.substring(5); // Remove "json."
                  } else if (actualPathBeforeDotPartial.startsWith("json[")) {
                    actualPath = actualPathBeforeDotPartial.substring(4); // Remove "json"
                  }
                  valueAtPath = actualPath
                    ? getValueAtPath(actualPath, targetNode.outputData)
                    : targetNode.outputData;
                } else {
                  // Get the value at the path before the partial field
                  valueAtPath = actualPathBeforeDotPartial
                    ? getValueAtPath(
                        actualPathBeforeDotPartial,
                        targetNode.outputData,
                      )
                    : targetNode.outputData;
                }

                // Auto-unwrap single-element arrays for better UX
                if (Array.isArray(valueAtPath) && valueAtPath.length === 1) {
                  valueAtPath = valueAtPath[0];
                }

                // Only show suggestions for objects (not arrays, since you can't type array indices)
                if (
                  valueAtPath &&
                  typeof valueAtPath === "object" &&
                  !Array.isArray(valueAtPath)
                ) {
                  const allKeys = Object.keys(valueAtPath);
                  const partialLower = partialField.toLowerCase();
                  const matchingKeys = allKeys.filter((key) =>
                    key.toLowerCase().includes(partialLower),
                  );

                  if (matchingKeys.length > 0) {
                    const options = matchingKeys.map((key) => {
                      const val = valueAtPath[key];
                      let type = "text";
                      if (Array.isArray(val)) type = "array";
                      else if (typeof val === "object" && val !== null)
                        type = "object";
                      else if (typeof val === "number") type = "number";
                      else if (typeof val === "boolean") type = "boolean";
                      else if (typeof val === "string") type = "string";

                      let detail = "";
                      if (Array.isArray(val)) {
                        detail = `Array[${val.length}]`;
                      } else if (typeof val === "object" && val !== null) {
                        const keys = Object.keys(val).slice(0, 3);
                        detail = `{${keys.join(", ")}}${Object.keys(val).length > 3 ? "..." : ""}`;
                      } else {
                        const str = String(val);
                        detail =
                          str.length > 20 ? str.substring(0, 20) + "..." : str;
                      }

                      const shouldAutoComplete = type === "object";
                      const isArray = type === "array";

                      const needsJsonPrefix =
                        pathBeforeDot === "json" && lastDotIndex === -1;

                      return {
                        label: key,
                        type,
                        detail,
                        apply: shouldAutoComplete
                          ? (
                              view: any,
                              _completion: any,
                              from: number,
                              to: number,
                            ) => {
                              const insertText = needsJsonPrefix
                                ? `json.${key}.`
                                : `${key}.`;
                              view.dispatch({
                                changes: { from, to, insert: insertText },
                                selection: { anchor: from + insertText.length },
                              });
                              setTimeout(() => {
                                startCompletion(view);
                              }, 50);
                            }
                          : isArray
                            ? (
                                view: any,
                                _completion: any,
                                from: number,
                                to: number,
                              ) => {
                                const insertText = needsJsonPrefix
                                  ? `json.${key}[`
                                  : `${key}[`;
                                view.dispatch({
                                  changes: { from, to, insert: insertText },
                                  selection: {
                                    anchor: from + insertText.length,
                                  },
                                });
                                setTimeout(() => {
                                  startCompletion(view);
                                }, 50);
                              }
                            : needsJsonPrefix
                              ? `json.${key}`
                              : key,
                        boost:
                          key.toLowerCase() === partialLower
                            ? 200
                            : key.toLowerCase().startsWith(partialLower)
                              ? 150
                              : 100,
                        render: renderCompletionWithIcon,
                      };
                    });

                    return {
                      from: context.pos - originalPartialLength,
                      options,
                      validFor: /^[a-zA-Z_$][\w$]*$/,
                    };
                  }
                }
              }
            }
          }

          // ============================================
          // EXISTING $json AUTOCOMPLETE (unchanged)
          // ============================================

          // 1. Typing inside array brackets - suggest array indices
          if (/\[\d*$/.test(normalizedExpr)) {
            const lastBracketPos = trimmedExpr.lastIndexOf("[");
            const pathBeforeBracket = trimmedExpr.substring(0, lastBracketPos);
            const afterBracket = trimmedExpr.substring(lastBracketPos + 1);

            // Only suggest if we're typing numbers inside the bracket
            if (!/^\d*$/.test(afterBracket)) {
              return null;
            }

            const valueAtPath = getValueAtPath(pathBeforeBracket);

            if (Array.isArray(valueAtPath)) {
              const options = valueAtPath.map((item, index) => {
                let detail = "";
                const isObject =
                  item && typeof item === "object" && !Array.isArray(item);
                const isArray = Array.isArray(item);

                if (isObject) {
                  const keys = Object.keys(item).slice(0, 3);
                  detail = `{${keys.join(", ")}${Object.keys(item).length > 3 ? "..." : ""}}`;
                } else if (isArray) {
                  detail = `Array[${item.length}]`;
                } else {
                  const str = String(item);
                  detail = str.length > 30 ? str.substring(0, 30) + "..." : str;
                }

                const shouldAutoCompleteDot = isObject;
                const shouldAutoCompleteBracket = isArray;

                return {
                  label: `${index}`,
                  type: "property",
                  detail,
                  apply: shouldAutoCompleteDot
                    ? (
                        view: any,
                        _completion: any,
                        from: number,
                        to: number,
                      ) => {
                        const insertText = `${index}].`;
                        view.dispatch({
                          changes: { from, to, insert: insertText },
                          selection: { anchor: from + insertText.length },
                        });
                        setTimeout(() => {
                          startCompletion(view);
                        }, 50);
                      }
                    : shouldAutoCompleteBracket
                      ? (
                          view: any,
                          _completion: any,
                          from: number,
                          to: number,
                        ) => {
                          const insertText = `${index}][`;
                          view.dispatch({
                            changes: { from, to, insert: insertText },
                            selection: { anchor: from + insertText.length },
                          });
                          setTimeout(() => {
                            startCompletion(view);
                          }, 50);
                        }
                      : `${index}]`,
                  boost: 100 - index,
                  render: renderCompletionWithIcon,
                };
              });

              return {
                from: context.pos - afterBracket.length,
                options: options.slice(0, 50),
                validFor: /^\d*$/,
              };
            }
          }

          // 2. Typing after a dot - suggest object properties
          if (normalizedExpr.match(/\.$/)) {
            const pathBeforeDot = normalizedExpr.slice(0, -1);
            const valueAtPath = getValueAtPath(pathBeforeDot);

            // Special case: If the value is an array, suggest bracket notation
            if (Array.isArray(valueAtPath)) {
              const options = valueAtPath.map((item, index) => {
                let detail = "";
                const isObject =
                  item && typeof item === "object" && !Array.isArray(item);
                const isArray = Array.isArray(item);

                if (isObject) {
                  const keys = Object.keys(item).slice(0, 3);
                  detail = `[${index}] {${keys.join(", ")}${Object.keys(item).length > 3 ? "..." : ""}}`;
                } else if (isArray) {
                  detail = `[${index}] Array[${item.length}]`;
                } else {
                  detail = `[${index}]`;
                }

                const shouldAutoCompleteDot = isObject;
                const shouldAutoCompleteBracket = isArray;

                return {
                  label: `[${index}]`,
                  type: "property",
                  detail,
                  apply: (
                    view: any,
                    _completion: any,
                    from: number,
                    to: number,
                  ) => {
                    let suffix = "";
                    if (shouldAutoCompleteDot) suffix = ".";
                    else if (shouldAutoCompleteBracket) suffix = "[";

                    const insertText = `[${index}]${suffix}`;
                    const replaceFrom = from - 1;
                    view.dispatch({
                      changes: { from: replaceFrom, to, insert: insertText },
                      selection: { anchor: replaceFrom + insertText.length },
                    });
                    if (shouldAutoCompleteDot || shouldAutoCompleteBracket) {
                      setTimeout(() => {
                        startCompletion(view);
                      }, 50);
                    }
                  },
                  boost: 100 - index,
                  render: renderCompletionWithIcon,
                };
              });

              return {
                from: context.pos,
                options: options.slice(0, 50),
                validFor: () => false,
              };
            }

            // If the value is an object, suggest its properties
            if (typeof valueAtPath === "object" && valueAtPath !== null) {
              const options: any[] = [];

              Object.entries(valueAtPath).forEach(([key, val]) => {
                let type = "text";
                if (Array.isArray(val)) {
                  type = "array";
                } else if (typeof val === "object" && val !== null) {
                  type = "object";
                } else if (typeof val === "number") {
                  type = "number";
                } else if (typeof val === "boolean") {
                  type = "boolean";
                } else if (typeof val === "string") {
                  type = "string";
                }

                const buildNestedPreview = (val: any): string => {
                  if (Array.isArray(val)) {
                    return `Array[${val.length}]`;
                  }

                  if (typeof val === "object" && val !== null) {
                    const keys = Object.keys(val).slice(0, 4);
                    return `{ ${keys.join(", ")}${Object.keys(val).length > 4 ? "..." : ""} }`;
                  }

                  const strValue = String(val);
                  return strValue.length > 20
                    ? strValue.substring(0, 20) + "..."
                    : strValue;
                };
                const detail = buildNestedPreview(val);

                const shouldAutoComplete = type === "object";
                const isArray = type === "array";

                options.push({
                  label: key,
                  type,
                  detail,
                  apply: shouldAutoComplete
                    ? (
                        view: any,
                        _completion: any,
                        from: number,
                        to: number,
                      ) => {
                        const insertText = key + ".";
                        view.dispatch({
                          changes: { from, to, insert: insertText },
                          selection: { anchor: from + insertText.length },
                        });
                        setTimeout(() => {
                          startCompletion(view);
                        }, 50);
                      }
                    : isArray
                      ? (
                          view: any,
                          _completion: any,
                          from: number,
                          to: number,
                        ) => {
                          const insertText = key + "[";
                          view.dispatch({
                            changes: { from, to, insert: insertText },
                            selection: { anchor: from + insertText.length },
                          });
                          setTimeout(() => {
                            startCompletion(view);
                          }, 50);
                        }
                      : key,
                  boost: 100,
                  render: renderCompletionWithIcon,
                });
              });

              if (options.length > 0) {
                return {
                  from: context.pos,
                  options,
                  validFor: /^[a-zA-Z_$][\w$]*$/,
                };
              }
            }
          }

          // 3. Typing after closing bracket without dot - suggest adding dot for object access
          if (/\]\s*$/.test(trimmedExpr) && !trimmedExpr.endsWith(".")) {
            const valueAtPath = getValueAtPath(trimmedExpr);

            if (
              valueAtPath &&
              typeof valueAtPath === "object" &&
              !Array.isArray(valueAtPath)
            ) {
              return {
                from: context.pos,
                options: [
                  {
                    label: ".",
                    type: "keyword",
                    detail: "Access properties",
                    apply: ".",
                    boost: 100,
                    render: renderCompletionWithIcon,
                  },
                ],
                validFor: () => false,
              };
            }
          }

          // 4. Typing "$" or partial "$json"/"$response" - suggest $json, $response and $()
          if (trimmedExpr && /^\$[a-z(]*$/i.test(trimmedExpr)) {
            const options: any[] = [];

            // Suggest $json if it matches
            if ("$json".startsWith(trimmedExpr.toLowerCase())) {
              const fields = availableFieldsRef.current;
              const rootField = fields.find((f) => f.path === "$json");
              const rootValue = rootField?.value;
              const isObjectOrArray =
                rootValue &&
                (typeof rootValue === "object" || Array.isArray(rootValue));

              options.push({
                label: "$json",
                type: "keyword",
                detail: "Access input data",
                apply: isObjectOrArray
                  ? (view: any, _completion: any, from: number, to: number) => {
                      const insertText = "$json.";
                      view.dispatch({
                        changes: { from, to, insert: insertText },
                        selection: { anchor: from + insertText.length },
                      });
                      setTimeout(() => {
                        startCompletion(view);
                      }, 50);
                    }
                  : "$json",
                boost: 100,
                render: renderCompletionWithIcon,
              });
            }

            // Suggest $response if the node passed responseData
            if (
              responseDataRef.current !== undefined &&
              responseDataRef.current !== null &&
              "$response".startsWith(trimmedExpr.toLowerCase())
            ) {
              const respData = responseDataRef.current;
              const isObjectOrArray =
                respData &&
                (typeof respData === "object" || Array.isArray(respData));

              options.push({
                label: "$response",
                type: "keyword",
                detail: "Access this node's response",
                apply: isObjectOrArray
                  ? (view: any, _completion: any, from: number, to: number) => {
                      const insertText = "$response.";
                      view.dispatch({
                        changes: { from, to, insert: insertText },
                        selection: { anchor: from + insertText.length },
                      });
                      setTimeout(() => {
                        startCompletion(view);
                      }, 50);
                    }
                  : "$response",
                boost: 98,
                render: renderCompletionWithIcon,
              });
            }

            // Suggest $() to reference other nodes
            if ("$(".startsWith(trimmedExpr) || trimmedExpr === "$") {
              const availableNodes = (availableNodesRef.current || []).filter(
                (node) =>
                  !FILTERED_NODE_TITLES.includes(node.title.toLowerCase()),
              );
              const nodeCount = availableNodes.length;

              options.push({
                label: "$()",
                type: "keyword",
                detail: `Reference other nodes (${nodeCount} available)`,
                apply: (
                  view: any,
                  _completion: any,
                  from: number,
                  to: number,
                ) => {
                  const insertText = "$(";
                  view.dispatch({
                    changes: { from, to, insert: insertText },
                    selection: { anchor: from + insertText.length },
                  });
                  setTimeout(() => {
                    startCompletion(view);
                  }, 50);
                },
                boost: 99,
                render: renderCompletionWithIcon,
              });
            }

            if (options.length > 0) {
              return {
                from: context.pos - trimmedExpr.length,
                options,
                validFor: /^\$[a-z(]*$/i,
              };
            }
          }

          return null;
        },
      ],
      activateOnTyping: true,
      closeOnBlur: true,
      icons: false,
      maxRenderedOptions: 100,
      defaultKeymap: true,
      interactionDelay: 0,
      updateSyncTime: 100,
      filterStrict: false,
    });
  }, []); // Empty deps - autocomplete function is fully stable via refs

  // Theme configuration for better readability
  const editorTheme = React.useMemo(() => {
    return EditorView.theme({
      "&": {
        fontSize: "11px",
        border: "1px solid hsl(var(--border))",
        borderRadius: "4px",
      },
      "&.cm-focused": {
        outline: "none",
        border: "1px solid hsl(var(--input))",
      },
      ".cm-content": {
        fontFamily:
          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        padding: "4px 8px",
        minHeight: minHeight,
        maxHeight: maxHeight,
        color: "var(--primary-green)",
        backgroundColor: validationError
          ? "rgba(239, 68, 68, 0.08)"
          : "transparent",
      },
      ".cm-line": {
        padding: "0",
        lineHeight: "1.5",
      },
      ".cm-placeholder": {
        color: "var(--muted-foreground)",
        fontStyle: "italic",
      },
      ".cm-scroller": {
        overflow: "auto",
        maxHeight: maxHeight,
      },
      ".cm-tooltip": {
        zIndex: "99999",
        overflow: "hidden !important",
      },
      ".cm-tooltip-autocomplete": {
        fontSize: "11px",
        border: "1px solid var(--muted)",
        borderRadius: "6px",
        boxShadow:
          "0 10px 25px -5px rgb(0 0 0 / 0.3), 0 8px 10px -6px rgb(0 0 0 / 0.3)",
        backgroundColor: "var(--background)",
        padding: "4px",
        zIndex: "99999",
        minWidth: "200px",
        width: "max-content",
        maxWidth: "70vw",
        maxHeight: "300px",
        overflow: "hidden !important",
      },
      ".cm-tooltip-autocomplete > ul": {
        maxHeight: "300px",
        overflowY: "auto",
        fontFamily:
          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      },
      ".cm-tooltip-autocomplete ul li": {
        padding: "6px 8px",
        borderRadius: "4px",
        cursor: "pointer",
        lineHeight: "1.5",
        transition: "background-color 0.1s ease",
      },
      ".cm-tooltip-autocomplete ul li:hover": {
        backgroundColor: "var(--light-gray)",
      },
      ".cm-tooltip-autocomplete ul li[aria-selected]": {
        backgroundColor: "var(--primary)",
        color: "#ffffff",
      },
      ".cm-tooltip-autocomplete ul li[aria-selected] .cm-completionLabel": {
        color: "#ffffff",
      },
      ".cm-tooltip-autocomplete ul li[aria-selected] .cm-completionIcon": {
        color: "#ffffff !important",
      },
      ".cm-completion-container": {
        display: "flex",
        alignItems: "center",
        width: "100%",
      },
      ".cm-completion-disabled": {
        opacity: "0.6",
        cursor: "not-allowed !important",
        pointerEvents: "none",
      },
      ".cm-tooltip-autocomplete ul li:has(.cm-completion-disabled)": {
        cursor: "not-allowed !important",
        backgroundColor: "transparent !important",
      },
      ".cm-tooltip-autocomplete ul li[aria-selected]:has(.cm-completion-disabled)":
        {
          backgroundColor: "var(--muted) !important",
          color: "var(--muted-foreground) !important",
        },
      ".cm-completionIcon": {
        marginRight: "6px",
        width: "14px",
        height: "14px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        fontSize: "10px",
        fontWeight: "700",
      },
      ".cm-completionIcon[data-type='string']": {
        color: "var(--primaryAmber)",
      },
      ".cm-completionIcon[data-type='text']": {
        color: "var(--primaryAmber)",
      },
      ".cm-completionIcon[data-type='number']": {
        color: "var(--primaryAmber)",
      },
      ".cm-completionIcon[data-type='boolean']": {
        color: "var(--primaryAmber)",
      },
      ".cm-completionIcon[data-type='array']": {
        color: "var(--primaryAmber)",
      },
      ".cm-completionIcon[data-type='object']": {
        color: "var(--primaryAmber)",
      },
      ".cm-completionIcon[data-type='keyword']": {
        color: "var(--primaryBlue)",
      },
      ".cm-completionIcon[data-type='method']": {
        color: "var(--primaryPurple)",
      },
      ".cm-completionIcon[data-type='property']": {
        color: "var(--primaryIndigo)",
      },
      ".cm-completionLabel": {
        fontFamily:
          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        fontSize: "11px",
      },
      ".cm-completionDetail": {
        fontStyle: "italic",
        color: "var(--muted-foreground)",
        marginLeft: "auto",
        paddingLeft: "8px",
        fontSize: "9px",
        overflow: "visible",
        textOverflow: "clip",
        whiteSpace: "nowrap",
        maxWidth: "none",
      },
      ".cm-tooltip-autocomplete ul li[aria-selected] .cm-completionDetail": {
        color: "#ffffff",
        opacity: 0.9,
      },
    });
  }, [minHeight, maxHeight, validationError]);

  const extensions = React.useMemo(() => {
    const tooltipExtensions =
      typeof document !== "undefined"
        ? [tooltips({ parent: getSharedTooltipHost(), position: "fixed" })]
        : [];

    return [
      ...tooltipExtensions,
      keymap.of(completionKeymap),
      autoCloseBrackets,
      fieldAutocomplete,
      editorTheme,
      syntaxHighlighting(expressionHighlighting),
      EditorView.lineWrapping,
      EditorView.updateListener.of((update) => {
        // Store the editor view ref for drag-and-drop
        if (!editorViewRef.current) {
          editorViewRef.current = update.view;
        }
        if (update.focusChanged) {
          setIsFocused(update.view.hasFocus);
        }
      }),
    ];
  }, [autoCloseBrackets, fieldAutocomplete, editorTheme]);

  // Drag-and-drop handlers
  const handleDragOver = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    setIsDragOver(true);
  }, []);

  const handleDragLeave = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    try {
      const dragData = e.dataTransfer.getData("application/json");
      if (!dragData) return;

      const parsedData = JSON.parse(dragData);
      const expression = parsedData.path || parsedData.value || "";

      if (!expression || !editorViewRef.current) return;

      const view = editorViewRef.current;

      // Get the position at the drop coordinates (mouse position)
      const dropPos = view.posAtCoords({ x: e.clientX, y: e.clientY });
      const insertPos =
        dropPos !== null ? dropPos : view.state.selection.main.head;

      // Insert the expression at the drop position
      view.dispatch({
        changes: { from: insertPos, to: insertPos, insert: expression },
        selection: { anchor: insertPos + expression.length },
      });

      // Notify parent of the change
      const newValue = view.state.doc.toString();
      onChangeRef.current(newValue);

      // Focus the editor
      view.focus();
    } catch (error) {
      console.error("Error handling drop:", error);
    }
  }, []);

  return (
    <div className={`w-full overflow-visible ${className}`}>
      <div
        className="border rounded transition-all duration-200 relative"
        style={{
          borderColor: validationError
            ? "var(--destructive)"
            : isDragOver
              ? "var(--primary)"
              : undefined,
          boxShadow: isDragOver
            ? "0 0 0 2px color-mix(in srgb, var(--primary) 20%, transparent)"
            : undefined,
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <CodeMirror
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          editable={!disabled}
          basicSetup={{
            lineNumbers: false,
            foldGutter: false,
            highlightActiveLineGutter: false,
            highlightActiveLine: false,
            dropCursor: true,
            allowMultipleSelections: false,
            indentOnInput: false,
            bracketMatching: true,
            closeBrackets: false,
            autocompletion: true, // MUST be true for our custom autocomplete to work!
            highlightSelectionMatches: false,
            searchKeymap: false,
            foldKeymap: false,
            completionKeymap: false,
            lintKeymap: false,
            drawSelection: true,
          }}
          extensions={extensions}
          minHeight={minHeight}
          maxHeight={maxHeight}
          theme="light"
          className="text-xs font-mono"
          autoFocus={false}
        />
      </div>

      {/* Result Preview */}
      {(resultPreview || isInvalidExpression) && hasExpression && (
        <div
          className="mt-2 rounded"
          style={{ backgroundColor: "var(--background)" }}
        >
          {!isInvalidExpression && (
            <div
              className="text-[10px] pl-2 mb-1 font-medium"
              style={{ color: "var(--muted-foreground)" }}
            >
              Result
            </div>
          )}
          <textarea
            value={resultPreview}
            readOnly
            className="w-full rounded px-2 py-1.5 text-[10px] font-mono resize-none focus:outline-none focus:ring-0 transition-all duration-300"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{
              height: isFocused || isHovered ? "150px" : "50px",
              backgroundColor: "var(--background)",
              color: isInvalidExpression
                ? "var(--destructive)"
                : "var(--primary-green)",
              border: isInvalidExpression
                ? "1px solid var(--destructive)"
                : "1px solid var(--border)",
              overflowY: "auto",
              transition: "height 0.3s ease",
            }}
          />
          <div className="flex justify-end mt-2">
            <CustomTooltip
              side="top"
              text={
                <div className="flex flex-col gap-2 p-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-medium">
                      Type{" "}
                      <code className="px-1 py-0.5 rounded bg-primary/10 text-primary font-mono text-[10px] font-semibold">
                        {"{{"}
                      </code>{" "}
                      for expressions
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-medium">
                      Access{" "}
                      <code className="px-1 py-0.5 rounded bg-primary/10 text-primary font-mono text-[10px] font-semibold">
                        .json
                      </code>{" "}
                      for transforms
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-medium">
                      Use{" "}
                      <code className="px-1 py-0.5 rounded bg-primary/10 text-primary font-mono text-[10px] font-semibold">
                        $(&quot;NodeName&quot;)
                      </code>{" "}
                      for node data
                    </span>
                  </div>
                  {responseData !== undefined && responseData !== null && (
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-medium">
                        Use{" "}
                        <code className="px-1 py-0.5 rounded bg-primary/10 text-primary font-mono text-[10px] font-semibold">
                          $response
                        </code>{" "}
                        for this node&apos;s response
                      </span>
                    </div>
                  )}
                </div>
              }
            >
              <div className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-primary/20 bg-primary/5 cursor-help hover:bg-primary/10 transition-colors">
                <HelpCircle size={15} className="text-primary" />
                <span className="text-[10px] font-medium text-primary">
                  Tips
                </span>
              </div>
            </CustomTooltip>
          </div>
        </div>
      )}
    </div>
  );
};

const ExpressionEditor = React.memo(
  ExpressionEditorComponent,
  (prevProps, nextProps) => {
    // Custom comparison to prevent unnecessary rerenders from React Flow
    if (
      prevProps.value !== nextProps.value ||
      prevProps.placeholder !== nextProps.placeholder ||
      prevProps.className !== nextProps.className ||
      prevProps.minHeight !== nextProps.minHeight ||
      prevProps.maxHeight !== nextProps.maxHeight ||
      prevProps.disabled !== nextProps.disabled ||
      prevProps.responseData !== nextProps.responseData
    ) {
      return false;
    }

    const prevFields = prevProps.availableFields || [];
    const nextFields = nextProps.availableFields || [];

    if (prevFields.length !== nextFields.length) {
      return false;
    }

    if (prevFields.length > 0) {
      for (let i = 0; i < prevFields.length; i++) {
        if (
          prevFields[i].path !== nextFields[i].path ||
          prevFields[i].value !== nextFields[i].value
        ) {
          return false;
        }
      }
    }

    // Compare availableNodes
    const prevNodes = prevProps.availableNodes || [];
    const nextNodes = nextProps.availableNodes || [];

    if (prevNodes.length !== nextNodes.length) {
      return false;
    }

    for (let i = 0; i < prevNodes.length; i++) {
      if (
        prevNodes[i].id !== nextNodes[i].id ||
        prevNodes[i].title !== nextNodes[i].title ||
        prevNodes[i].outputData !== nextNodes[i].outputData
      ) {
        return false;
      }
    }

    return true;
  },
);

ExpressionEditor.displayName = "ExpressionEditor";
export default ExpressionEditor;
